import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { FirGhostOwnershipService } from './fir-ghost-ownership.service';
import { Subject, takeUntil } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams
} from '../../../shared/workspace-element-context';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
    selector: 'gtw-fir-ghost-ownership',
    templateUrl: './fir-ghost-ownership.component.html',
    styleUrls: ['./fir-ghost-ownership.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirGhostOwnershipComponent implements OnInit, OnDestroy {

  /**
   * Single channel for both confirm and cancel — matches the `fir-form-regen`
   * canonical pattern. `createCustomElement` converts this to a native
   * `request-grid-update` CustomEvent on the host, which the AngularJS
   * bridge already listens for and always calls `$uibModalInstance.close()`.
   */
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: ''
  };
  /** Shape required by `FirGhostOwnershipService`: `globalParams` + `filterParams`. */
  globalService: any = {};
  logged_in_user: string = '';

  showModal: boolean = true;
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to run the ownership process?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  private readonly destroy$ = new Subject<void>();
  private saveRequestSeq = 0;

  constructor(
    private firGhostOwnershipService: FirGhostOwnershipService,
    private gtwSnackbarService: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.gtwWorkspace.setTabActive.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyWorkspaceContext();
    });
    this.gtwGlobal.globalParamsChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyWorkspaceContext();
    });
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      globalParams: mergedGp,
      filterParams: fp
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    const mergedGc = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = {
      admin: mergedGc.admin ?? '',
      api: (mergedGc.api || mergedGc.base_url || '') as string,
      custom: mergedGc.custom ?? '',
      efile: mergedGc.efile ?? '',
      pdfEngine: mergedGc.pdfEngine ?? '',
      bulkPdfEngine: mergedGc.bulkPdfEngine ?? ''
    };
    this.logged_in_user = this.gtwApp?.USER_SETTINGS?.user?.sso_id || '';
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCancel(): void {
    this.showModal = false;
    this.requestGridUpdate.emit('cancel');
    this.cdr.markForCheck();
  }

  onConfirm(): void {
    try {
      this.modalConfig = { ...this.modalConfig, loading: true };
      this.cdr.markForCheck();

      const returnClobSettingsObj: any = {};
      returnClobSettingsObj['sso_id'] = this.logged_in_user;

      this.triggerOwnership(returnClobSettingsObj);
    } catch (_e) {
      this.modalConfig = { ...this.modalConfig, loading: false };
      this.gtwSnackbarService.openSnackBar('Error occurred during ownership process setup', 'gtw-snackbar--red');
      this.cdr.markForCheck();
    }
  }

  private triggerOwnership(returnClobSettingsObj: any): void {
    const baseUrl = (this.baseURLs.api || '').toString();
    if (!baseUrl) {
      this.modalConfig = { ...this.modalConfig, loading: false };
      this.gtwSnackbarService.openSnackBar('Unable to resolve API base', 'gtw-snackbar--red');
      this.cdr.markForCheck();
      return;
    }

    const seq = ++this.saveRequestSeq;
    this.firGhostOwnershipService.triggerOwnership(
      returnClobSettingsObj,
      baseUrl,
      this.globalService
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result: any) => {
        if (seq !== this.saveRequestSeq) {
          return;
        }
        this.modalConfig = { ...this.modalConfig, loading: false };

        if (result?.callSuccess === '1') {
          if (result.errorMessage && result.errorMessage !== 'null') {
            this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar('Ownership process has been successfully completed', 'gtw-snackbar--green');
          }
        } else {
          this.gtwSnackbarService.openSnackBar('Ownership process failed', 'gtw-snackbar--red');
        }

        this.requestGridUpdate.emit('refresh');
        this.cdr.markForCheck();
      },
      error: (error: HttpErrorResponse) => {
        if (seq !== this.saveRequestSeq) {
          return;
        }
        this.modalConfig = { ...this.modalConfig, loading: false };

        let errorMessage = 'Error occurred during ownership process';
        if (error?.error?.errorMessage) {
          errorMessage = error.error.errorMessage;
        }
        this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    });
  }
}
